/**
 * Which card sits on top of the stack, and in what order the rest follow.
 *
 * The rail's footer has room for one card. Everything the machinery could say
 * has to compete for it, and the order is a claim about the operator's day:
 *
 *   1. SOMETHING BROKE. A failed transcription or a dead sync is the only kind
 *      of background news that costs something to miss, so it outranks
 *      everything, newest first.
 *   2. SOMETHING IS RUNNING. Live progress, while it is live. This is the card
 *      the operator looks for after pressing a button.
 *   3. EVERYTHING IS FINE. One card carrying every engine's pulse. Always
 *      present, which is the point: the old card hid itself when nothing was
 *      running, so "idle" and "dead" drew exactly the same picture - nothing.
 *
 * Pure module, no React. The ordering is the part worth testing, and it is
 * testable without mounting a rail.
 */
import type { BgBeat, BgEngine, BgAlert, BgJob } from "../../../lib/background";
import { ENGINE_LATE_MS } from "../../../lib/monitoringView";

const MINUTE = 60 * 1000;

/**
 * When each engine counts as late, measured against how often it is supposed to
 * run rather than against one shared number.
 *
 * One threshold for all four does not work, and the failure is not symmetric: a
 * number loose enough for the register import (which runs about once a day)
 * would let the calls worker be dead for two days before anyone noticed, and a
 * number tight enough for the calls worker marks the register late every single
 * day. A card that cries wolf daily is worse than no card.
 *
 * Each value is roughly three times the engine's own cadence, which absorbs one
 * missed cycle and a slow run without absorbing an outage.
 */
export const STALE_AFTER_MS: Record<BgEngine, number> = {
  /** 60-second tick. */
  calls: 15 * MINUTE,
  /** Polls every two minutes by default. */
  mail: 30 * MINUTE,
  /** Hourly batches, and a batch of twenty sites can take minutes. */
  scanner: 180 * MINUTE,
  /** An open-data import: about once a day, sometimes weekly. */
  register: 48 * 60 * MINUTE,
  /** Saidiseire: the desk strip's own threshold (`ENGINE_LATE_MS`, where the
   *  worst tick length is worked out), so the shell card and the desk never
   *  disagree about the same beat. */
  monitoring: ENGINE_LATE_MS,
  /** Saidiseire's slow jobs: a five-minute wake, and a wake that downloads the
   *  vulnerability feed may take eight minutes on its own. */
  monitoringJobs: 30 * MINUTE,
};

export type BgCard =
  | { key: string; tone: "alert"; alert: BgAlert }
  | { key: string; tone: "job"; job: BgJob }
  | { key: "beats"; tone: "beats"; beats: BgBeat[]; late: BgBeat[] };

export interface CardInput {
  jobs: BgJob[];
  attention: BgAlert[];
  beats: BgBeat[];
  /** Injected so the test does not depend on the clock. */
  now?: number;
}

/** Which engine a running job proves is alive. Campaigns have none of their own. */
const JOB_ENGINE: Partial<Record<BgJob["kind"], BgEngine>> = {
  scanner: "scanner",
  bulk: "scanner",
  calls: "calls",
  register: "register",
};

/**
 * Engines that are switched on and have gone quiet.
 *
 * Four things are deliberately NOT late, and each of them was reported as late
 * once, which is how they got written down:
 *
 *   SWITCHED OFF. It was told not to run. Telling somebody that a thing they
 *   disabled has not run is how a card teaches people to ignore it.
 *
 *   NEVER REPORTED. No pulse is not a dead engine, it is an unmeasured one -
 *   which is what every engine is for the first minutes after a deploy. Claiming
 *   an outage on the strength of never having looked is a lie the card cannot
 *   afford; it says "waiting for the first run" instead.
 *
 *   WORKING RIGHT NOW. A batch in flight is the strongest evidence of life
 *   there is, and it outranks any timestamp. The scanner reported itself silent
 *   while it was mid-batch, because its pulse was only written when the batch
 *   finished.
 *
 *   RESTING ON A SPENT BUDGET. The scanner's daily cap is a ceiling on sites
 *   fetched per calendar day; once it is reached the autopilot declines every
 *   tick and deliberately writes no run row for a decline it was not asked for.
 *   So the newest run row stops moving the moment the day's work is DONE, and
 *   the card called the engine dead for the nine hours between the cap and
 *   midnight - every day, which is the exact cadence at which a warning stops
 *   being read. Only the server can tell the two apart, so it says which it is.
 */
export function lateBeats(beats: BgBeat[], now = Date.now(), jobs: BgJob[] = []): BgBeat[] {
  const alive = new Set(jobs.map((j) => JOB_ENGINE[j.kind]).filter(Boolean));
  return beats.filter((b) => {
    if (!b.enabled) return false;
    if (alive.has(b.key)) return false;
    // AFTER the health check on purpose: resting explains an old timestamp, it
    // does not excuse a run that failed. Ordered the other way, a spent daily
    // cap would hide a broken engine until midnight.
    if (!b.ok) return true;
    if (b.resting) return false;
    if (!b.at) return false;
    return now - new Date(b.at).getTime() > STALE_AFTER_MS[b.key];
  });
}

/**
 * The engine whose pulse is closest to going stale, measured against its own
 * cadence rather than in absolute minutes.
 *
 * The pulse card has one line for four engines, so it has to choose, and the
 * obvious choices are both bad. The newest pulse is always the calls worker,
 * which ticks every minute and is therefore the least informative thing on the
 * card. The oldest in absolute terms is always the register, which imports once
 * a day and is fine. Dividing each age by what that engine is allowed makes the
 * line answer a real question: of everything back there, what is furthest from
 * having reported, and when did it last run.
 */
export function weakestBeat(beats: BgBeat[], now = Date.now()): BgBeat | null {
  let worst: BgBeat | null = null;
  let worstRatio = -1;
  for (const b of beats) {
    if (!b.enabled || !b.at) continue;
    const ratio = (now - new Date(b.at).getTime()) / STALE_AFTER_MS[b.key];
    if (ratio > worstRatio) {
      worstRatio = ratio;
      worst = b;
    }
  }
  return worst;
}

export function buildCards(input: CardInput): BgCard[] {
  const now = input.now ?? Date.now();
  const cards: BgCard[] = [];

  for (const alert of input.attention) {
    cards.push({ key: `alert:${alert.key}`, tone: "alert", alert });
  }
  for (const job of input.jobs) {
    cards.push({ key: `job:${job.key}`, tone: "job", job });
  }
  cards.push({
    key: "beats",
    tone: "beats",
    beats: input.beats,
    late: lateBeats(input.beats, now, input.jobs),
  });

  return cards;
}

/** True when the rail's collapsed dot should pulse: work in flight or trouble. */
export function railBusy(input: CardInput): boolean {
  return input.jobs.length > 0 || input.attention.length > 0;
}
